import React, { useMemo, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { GET_PRODUCT_SELECTION_GAPS } from "./graphql";
import { GET_BOOKINGS } from "../AdminBookings/graphql";
import OrderDetailsModal from "../AdminBookings/OrderDetailsModal";

function formatDate(d) {
  return d.toISOString().split("T")[0];
}

function defaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 6); // last 7 days, inclusive
  return { startDate: formatDate(start), endDate: formatDate(end) };
}

function formatOrderDate(value) {
  if (!value) return "N/A";
  const num = Number(value);
  const date = Number.isNaN(num) ? new Date(value) : new Date(num);
  return date.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });
}

const GAP_LABELS = {
  NOT_SUBMITTED: "No selection submitted",
  ALTERNATE_NOT_CHOSEN: "Alternate not chosen",
  PRODUCT_ITEM_REMOVED: "Item removed by employee",
  APPROVAL_OVERRIDDEN: "Approval overridden",
  STOCK_NOT_DEDUCTED: "Stock not deducted",
  INSUFFICIENT_STOCK: "Insufficient stock (older bookings)",
};

// Why a product wasn't deducted from the professional's stock.
const STOCK_GAP_LABELS = {
  NO_STOCK: "No stock",
  INSUFFICIENT_STOCK: "Not enough stock",
  NOT_SCANNED: "Not scanned",
};

// An order can have stock gaps whatever its main gap type is.
const hasStockGaps = (g) => (g.stockGaps || []).length > 0;

const GAP_STYLES = {
  NOT_SUBMITTED: "bg-red-100 text-red-700",
  ALTERNATE_NOT_CHOSEN: "bg-amber-100 text-amber-700",
  PRODUCT_ITEM_REMOVED: "bg-pink-100 text-pink-700",
  APPROVAL_OVERRIDDEN: "bg-purple-100 text-purple-700",
  STOCK_NOT_DEDUCTED: "bg-orange-100 text-orange-700",
  INSUFFICIENT_STOCK: "bg-orange-100 text-orange-700",
};

const ProductSelectionGaps = ({ employees }) => {
  const [{ startDate, endDate }, setDateRange] = useState(defaultDateRange());
  const [employeeId, setEmployeeId] = useState("ALL");
  const [gapTypeFilter, setGapTypeFilter] = useState("ALL");

  const [viewingOrderId, setViewingOrderId] = useState(null);
  const [viewingBooking, setViewingBooking] = useState(null);
  const [fetchBooking] = useMutation(GET_BOOKINGS);

  const { data, loading, error } = useQuery(GET_PRODUCT_SELECTION_GAPS, {
    variables: {
      startDate,
      endDate,
      employeeId: employeeId === "ALL" ? null : employeeId,
    },
    fetchPolicy: "network-only",
  });

  const response = data?.getProductSelectionGaps;
  const gaps = response?.gaps || [];

  const visibleGaps = useMemo(() => {
    if (gapTypeFilter === "ALL") return gaps;
    if (gapTypeFilter === "STOCK_NOT_DEDUCTED") return gaps.filter(hasStockGaps);
    return gaps.filter((g) => g.gapType === gapTypeFilter);
  }, [gaps, gapTypeFilter]);

  const counts = useMemo(() => {
    const c = {
      NOT_SUBMITTED: 0,
      ALTERNATE_NOT_CHOSEN: 0,
      PRODUCT_ITEM_REMOVED: 0,
      APPROVAL_OVERRIDDEN: 0,
      STOCK_NOT_DEDUCTED: 0,
      INSUFFICIENT_STOCK: 0,
    };
    gaps.forEach((g) => {
      if (g.gapType !== "STOCK_NOT_DEDUCTED" && c[g.gapType] !== undefined) c[g.gapType] += 1;
      if (hasStockGaps(g)) c.STOCK_NOT_DEDUCTED += 1;
    });
    return c;
  }, [gaps]);

  const openOrderDetails = async (orderId) => {
    setViewingOrderId(orderId);
    setViewingBooking(null);
    try {
      const res = await fetchBooking({ variables: { data: { id: orderId } } });
      const booking = res?.data?.getBookings?.orders?.[0];
      setViewingBooking(booking || null);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="space-y-6">
      {/* FILTERS */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:flex-wrap bg-white border border-gray-100 rounded-2xl shadow-sm p-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            From
          </label>
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) =>
              setDateRange((prev) => ({ ...prev, startDate: e.target.value }))
            }
            className="px-3 py-2 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            To
          </label>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={formatDate(new Date())}
            onChange={(e) =>
              setDateRange((prev) => ({ ...prev, endDate: e.target.value }))
            }
            className="px-3 py-2 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Employee
          </label>
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            <option value="ALL">All Employees</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Gap Type
          </label>
          <select
            value={gapTypeFilter}
            onChange={(e) => setGapTypeFilter(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            <option value="ALL">All Gaps</option>
            {Object.entries(GAP_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-600" />
          <span className="ml-3 text-gray-600 text-sm">Loading...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-4">
          {error.message}
        </div>
      )}

      {response?.status === "ERROR" && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-4">
          {response.message}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* SUMMARY CARDS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {Object.entries(GAP_LABELS).map(([value, label]) => (
              <div
                key={value}
                className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100"
              >
                <p className="text-xs font-medium text-gray-500">{label}</p>
                <p
                  className={`mt-2 text-2xl font-semibold ${
                    counts[value] > 0 ? "text-amber-600" : "text-gray-900"
                  }`}
                >
                  {counts[value]}
                </p>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-500">
            "No selection submitted" means the employee never ran the pre-start product
            confirmation for a booking that started/completed. "Alternate not chosen" means
            a configured alternate group (e.g. Shampoo A or B) had no explicit pick, so every
            option in it got deducted - the customer's disclosed list is ambiguous.
            "Approval overridden" means the customer never tapped Approve; the 2-minute
            soft-gate let the booking proceed anyway. "Item removed by employee" means the
            employee removed a recipe/manual-count item in the product-list review screen -
            allowed, but flagged here for review; open the booking's Inventory tab to restore
            it if it should have been used.
          </p>

          {/* GAPS TABLE */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            {visibleGaps.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                No gaps for the selected filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-4 py-2">Booking</th>
                      <th className="px-4 py-2">Date</th>
                      <th className="px-4 py-2">Employee</th>
                      <th className="px-4 py-2">Customer</th>
                      <th className="px-4 py-2">Services</th>
                      <th className="px-4 py-2">Gap</th>
                      <th className="px-4 py-2">Sent At</th>
                      <th className="px-4 py-2">Approved At</th>
                      <th className="px-4 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {visibleGaps.map((g) => (
                      <tr key={g.orderId}>
                        <td className="px-4 py-2">
                          <button
                            type="button"
                            onClick={() => openOrderDetails(g.orderId)}
                            className="text-blue-600 hover:underline font-medium"
                          >
                            {g.bookingId || g.orderId.slice(0, 8)}
                          </button>
                        </td>
                        <td className="px-4 py-2 text-gray-500">
                          {formatOrderDate(g.bookingDate)}
                        </td>
                        <td className="px-4 py-2">{g.employeeName || "—"}</td>
                        <td className="px-4 py-2">{g.customerName || "—"}</td>
                        <td className="px-4 py-2 text-xs text-gray-500">
                          {(g.serviceNames || []).join(", ")}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              GAP_STYLES[g.gapType] || "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {GAP_LABELS[g.gapType] || g.gapType}
                          </span>
                          {hasStockGaps(g) && (
                            <ul className="mt-1 space-y-0.5 text-xs text-orange-800">
                              {g.stockGaps.map((sg, i) => (
                                <li key={i}>
                                  <span className="font-medium">{sg.categoryName}</span>
                                  {" – "}
                                  {STOCK_GAP_LABELS[sg.gap] || sg.gap}
                                  {sg.detail ? `: ${sg.detail}` : ""}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-500">
                          {g.productListSentAt ? formatOrderDate(g.productListSentAt) : "—"}
                        </td>
                        <td className="px-4 py-2 text-gray-500">
                          {g.productListApprovedAt
                            ? formatOrderDate(g.productListApprovedAt)
                            : "—"}
                        </td>
                        <td className="px-4 py-2">
                          <button
                            type="button"
                            onClick={() => openOrderDetails(g.orderId)}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {viewingOrderId && (
        viewingBooking ? (
          <OrderDetailsModal
            booking={viewingBooking}
            onClose={() => {
              setViewingOrderId(null);
              setViewingBooking(null);
            }}
          />
        ) : (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-2xl shadow-xl p-6 flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-600" />
              <span className="text-sm text-gray-600">Loading order...</span>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default ProductSelectionGaps;
